import axios from 'axios';
import * as types from 'legacy/src/constants/types';
import { wizardVideo, getExam } from 'legacy/src/queries';
import log from 'chameleon/ui-stack/utilities/log';
import SplitIOHandler from 'chameleon/ui-stack/utilities/split-io-handler';

/**
 * Returns requested wizard query.
 * @param {string} The type of wizard query to perform.
 * @return {object<Promise>}
 * */
function getWizardInfo(wizardType) {
  const data = {
    query: null,
  };
  switch (wizardType) {
    case 'video':
      data.query = wizardVideo;
      break;
    case 'examWindow':
      data.query = getExam;
      break;
    default:
      break;
  }

  if (!data.query) {
    return Promise.reject(
      new Error(`Invalid or missing wizardType arg: ${wizardType}`),
    );
  }

  /**
   * Make axios call to service-wizards to see what needs to be displayed.
   */
  return axios({
    method: 'post',
    url: `${FYM_WIZARDS_BACKEND_URL}/graphql/`,
    headers: {
      'Content-Type': 'application/json',
    },
    data,
  });
}

/**
 * @param {object} [props] needed to get router object.
 * @param {object} video Video data for welcome video wizard.
 * @param {boolean} [questionsOnly = false] Bypasses the video and display questions view.
 * @param {string} [queryString = ''] Optional query string to append to URL.
 *                                    Note: currently used to pass user initId.
 */
function goToWelcomeVideo(
  props,
  video,
  questionsOnly = false,
  queryString = '',
) {
  const qs =
    queryString.length && !/^&/.test(queryString)
      ? `&${queryString}`
      : queryString;
  const url =
    `/wizards?wizardType=${questionsOnly ? 'videoResponse' : 'video'}` +
    `&info=${JSON.stringify(video)}${qs}&path=${window.location.href}`;

  props.router.push(url);
}

/**
 * @param {object} [props] allows to get props of current studentHome. For future usage.
 * This function is a helper to check if wizard is disabled.
 */
function checkIfWizardsDisabled(props) {
  const {
    subjectData: { isPreApSubject },
  } = props;
  return !FEATUREFLAGS.wizards || isPreApSubject;
}

/**
 * @param {boolean} [stateUpdateOnly = false] Allows for bypass of wizard redirects.
 * @param {Object.<*>} props Props object of current component.
 */
function checkWizardOnLoadAndRedirect(props) {
  const { dispatch } = props;
  if (checkIfWizardsDisabled(props)) {
    dispatch({ type: types.WIZARDS_DISABLE });
    return;
  }
  dispatch({ type: types.WIZARDS_INFO_FETCH });
  getWizardInfo('video')
    .then((response) => {
      const video = response && response.data.data.video;
      /**
       * Sets wizard video response to state for
       * student carousel and conditional display.
       */
      dispatch({
        type: types.WIZARDS_INFO_FULLFILLED,
        wizardType: 'video',
        video,
      });

      const { isImpersonatingWelcomeVideoFirstTime, isImpersonating } = props;
      const { isFirstTime } = video || {};

      let withVideoAndIsFirstTime;
      if (video && isImpersonating) {
        withVideoAndIsFirstTime =
          isFirstTime && isImpersonatingWelcomeVideoFirstTime;
      } else {
        withVideoAndIsFirstTime = !!video && isFirstTime;
      }

      if (withVideoAndIsFirstTime) {
        /*
         * Redirect to welcome video.
         * */
        goToWelcomeVideo(
          props,
          video,
          false,
          `&userId=${props.userData.me.initId}`,
        );
      }
    })
    .catch((error) => log('error', error));
}

function loadWizards(props, dispatch) {
  const { email, initId } = props.userData.me;
  const {
    subjectData: { isPreApSubject },
  } = props;
  dispatch({ type: types.WIZARDS_INFO_FETCH });
  const flagHandler = new SplitIOHandler({
    key: initId,
    attributes: { email },
    splitName: ['qb_training'],
    callback: (treatment) => {
      if (treatment) {
        if (treatment.qb_training === 'show_wizards') {
          /**
           * Show wizards if enabled.
           */
          dispatch(checkWizardOnLoadAndRedirect(isPreApSubject, props));
        } else {
          /**
           * If wizards are disabled, set wizards disabled boolean to state.
           */
          dispatch({
            type: types.WIZARDS_DISABLE,
          });
        }
      } else {
        /**
         * If no response was received, set wizards disabled boolean to state.
         */
        dispatch({
          type: types.WIZARDS_DISABLE,
        });
      }
    },
  });

  flagHandler.invoke();
}

export function wizardsEntryPoint(props) {
  return (dispatch) => {
    if (checkIfWizardsDisabled(props)) {
      dispatch({ type: types.WIZARDS_DISABLE });
      return;
    }
    dispatch({ type: types.WIZARDS_INFO_FETCH });
    getWizardInfo('examWindow')
      .then((response) => {
        const { examResponsesWindow } = response.data.data || {};
        const examResponsesWindowOpen =
          !!examResponsesWindow && examResponsesWindow.isExamResponsesOn;
        /**
         * Get the exam response window and set to state for display logic
         */
        dispatch({
          type: types.WIZARDS_INFO_FULLFILLED,
          wizardType: 'examWindow',
          examResponsesWindowOpen,
        });
        loadWizards(props, dispatch);
      })
      .catch((error) => log('error', error));
  };
}

export function getWelcomeVideoResponses(props = {}) {
  const { dispatch } = props;
  dispatch(getWelcomeVideo(props, true));
}

/*
 * @param {Object.<*>} props Props object of current component.
 * @param {boolean} [questionsOnly = false] Bypasses the video and display questions view.
 */
function getWelcomeVideo(props, questionsOnly = false) {
  const { dispatch } = props;
  dispatch({ type: types.WIZARDS_INFO_FETCH });
  getWizardInfo('video')
    .then((response) => {
      const video = response && response.data.data.video;
      dispatch({
        type: types.WIZARDS_INFO_FULLFILLED,
        wizardType: 'video',
        video,
      });
      if (video) {
        goToWelcomeVideo(
          props,
          video,
          questionsOnly,
          `&userId=${props.userData.me.initId}`,
        );
      }
    })
    .catch((error) => log('error', error));
}

export { checkWizardOnLoadAndRedirect, getWelcomeVideo };
